// src/services/calendarService.ts
/** .tuMetrics {"tu1": 0.85, "tu2": 0.87, "tu3": 8, "notes": "Calendar and Google Calendar event service", "tuVersion": "2.5"} * .help "Company calendar events, Google Calendar create/update for activities and card schedule, reclaim time." */
import { supabase } from '../lib/supabase';
import useUserStore from '@/store/userStore';
import { ActivityType } from './liveActivityService';
import { listGoogleCalendarEvents, GoogleCalendarEvent } from '../lib/googleWorkspace';
import {
  type MeetingOptions,
  plannedMeetingWindow,
  realEndForCalendarRow,
  realStartForCalendarRow,
} from '../features/time/meetingOptions';

export interface CalendarEvent {
  id?: string;
  company_id: string;
  created_by_user_id: string;
  meet_message_id?: string;
  /** Named meeting (chat_sessions) this row mirrors. */
  chat_session_id?: string | null;
  /** Project the meeting is about; Join inherits it. */
  project_id?: string | null;
  /** Google Calendar event created for this meeting. */
  google_event_id?: string | null;
  title: string;
  description?: string;
  start_time: string; // ISO timestamp
  end_time?: string; // ISO timestamp
  duration?: number; // in seconds
  participant_count?: number;
  participants?: Array<{ userId?: string; email?: string; name?: string }>;
  external_participants?: string[]; // Array of email addresses
  status: 'scheduled' | 'active' | 'completed' | 'cancelled';
  is_recurring?: boolean;
  recurrence_pattern?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    standard_time?: string; // HH:MM format
    days?: number[]; // For weekly/monthly: [0-6] for days of week
  };
  show_on_team_calendar: boolean;
  transcript_url?: string;
  transcript_doc_id?: string;
  meet_url?: string;
  conference_id?: string;
}

/**
 * Create a calendar event from a Meet session
 */
export async function createCalendarEventFromMeet(
  meetMessageId: string,
  meetUrl: string,
  title: string,
  participants: string[],
  externalParticipants: string[] = [],
  options?: {
    showOnTeamCalendar?: boolean;
    isRecurring?: boolean;
    recurrencePattern?: {
      frequency: 'daily' | 'weekly' | 'monthly';
      standardTime?: string;
      days?: number[];
    };
    conferenceId?: string;
    /** Project the meeting is about (room project). Join inherits it. */
    projectId?: string | null;
    /** Planned length; default 30. Real end is written when the last attendee leaves. */
    durationMinutes?: number;
    /** ISO start; default now. */
    scheduledStart?: string;
  }
): Promise<{ success: boolean; eventId?: string; error?: string }> {
  try {
    const { currentUser } = useUserStore.getState();
    if (!currentUser?.companyId) {
      return { success: false, error: 'User not authenticated' };
    }

    // Get participant user IDs from emails
    const { data: participantUsers, error: participantError } = await supabase
      .from('users')
      .select('id, email, name')
      .in('email', participants)
      .eq('company_id', currentUser.companyId);

    if (participantError) {
      console.error('Error fetching participant users:', participantError);
    }

    // Create participant objects
    const participantObjects = (participantUsers || []).map(user => ({
      userId: user.id,
      email: user.email,
      name: user.name,
    }));

    // Planned block: scheduled start (or now) + duration (30 by default). Real end comes later.
    const window = plannedMeetingWindow(
      { scheduledStart: options?.scheduledStart, durationMinutes: options?.durationMinutes },
      Date.now(),
    );
    const startTime = window.startIso;
    const endTime = window.endIso;

    // Extract conference ID from Meet URL
    const conferenceId = options?.conferenceId || meetUrl.split('/').pop() || '';

    const eventData: CalendarEvent = {
      company_id: currentUser.companyId,
      created_by_user_id: currentUser.id,
      meet_message_id: meetMessageId,
      project_id: options?.projectId ?? null,
      title,
      start_time: startTime,
      end_time: endTime,
      duration: window.durationMinutes * 60,
      participant_count: participants.length + externalParticipants.length,
      participants: participantObjects,
      external_participants: externalParticipants,
      status: 'scheduled',
      is_recurring: options?.isRecurring || false,
      recurrence_pattern: options?.recurrencePattern ? {
        frequency: options.recurrencePattern.frequency,
        standard_time: options.recurrencePattern.standardTime,
        days: options.recurrencePattern.days,
      } : undefined,
      show_on_team_calendar: options?.showOnTeamCalendar !== false, // Default to true
      meet_url: meetUrl,
      conference_id: conferenceId,
    };

    const { data, error } = await supabase
      .from('company_calendar_events')
      .insert(eventData)
      .select()
      .single();

    if (error) {
      console.error('Error creating calendar event:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ Calendar event created:', data.id);
    return { success: true, eventId: data.id };
  } catch (err: any) {
    console.error('Error creating calendar event:', err);
    return { success: false, error: err.message || 'Failed to create calendar event' };
  }
}

/**
 * Update calendar event status
 */
export async function updateCalendarEventStatus(
  eventId: string,
  status: 'scheduled' | 'active' | 'completed' | 'cancelled',
  updates?: {
    endTime?: string;
    duration?: number;
    participantCount?: number;
    transcriptUrl?: string;
    transcriptDocId?: string;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const updateData: any = {
      status,
      updated_at: new Date().toISOString(),
    };

    if (updates?.endTime) updateData.end_time = updates.endTime;
    if (updates?.duration !== undefined) updateData.duration = updates.duration;
    if (updates?.participantCount !== undefined) updateData.participant_count = updates.participantCount;
    if (updates?.transcriptUrl) updateData.transcript_url = updates.transcriptUrl;
    if (updates?.transcriptDocId) updateData.transcript_doc_id = updates.transcriptDocId;

    const { error } = await supabase
      .from('company_calendar_events')
      .update(updateData)
      .eq('id', eventId);

    if (error) {
      console.error('Error updating calendar event:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error('Error updating calendar event:', err);
    return { success: false, error: err.message || 'Failed to update calendar event' };
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * One calendar row per named meeting (chat_sessions). Insert on create, update on edit, keyed by
 * chat_session_id. Toggling "show on team calendar" off keeps the row but hides it.
 */
export async function upsertMeetingCalendarEvent(args: {
  chatSessionId: string;
  companyId: string;
  createdByUserId: string;
  title: string;
  /** Currents user ids in the meeting. */
  participantUserIds: string[];
  options: MeetingOptions;
  meetUrl?: string | null;
}): Promise<{ success: boolean; eventId?: string; error?: string }> {
  try {
    const { allUsers } = useUserStore.getState();
    const participants = args.participantUserIds
      .map((id) => allUsers.find((u) => u.id === id))
      .filter((u): u is NonNullable<typeof u> => !!u && !u.isAgent)
      .map((u) => ({ userId: u.id, email: u.email, name: u.name }));
    const external = args.options.externalParticipants ?? [];
    const window = plannedMeetingWindow(args.options, Date.now());

    const { data: existing } = await supabase
      .from('company_calendar_events')
      .select('id, status')
      .eq('chat_session_id', args.chatSessionId)
      .maybeSingle();

    const patch: Partial<CalendarEvent> & { updated_at?: string } = {
      title: args.title,
      project_id: args.options.projectId ?? null,
      participant_count: participants.length + external.length,
      participants,
      external_participants: external,
      is_recurring: args.options.isRecurring === true,
      recurrence_pattern:
        args.options.isRecurring && args.options.recurrenceFrequency
          ? {
              frequency: args.options.recurrenceFrequency,
              standard_time: args.options.standardTime,
              days: args.options.recurrenceDays,
            }
          : undefined,
      show_on_team_calendar: args.options.showOnTeamCalendar === true,
      meet_url: args.meetUrl ?? undefined,
      updated_at: new Date().toISOString(),
    };
    if (args.options.googleEventId !== undefined) patch.google_event_id = args.options.googleEventId;

    if (existing?.id) {
      // Only move the planned window while nobody has joined yet.
      if (existing.status === 'scheduled') {
        patch.start_time = window.startIso;
        patch.end_time = window.endIso;
        patch.duration = window.durationMinutes * 60;
      }
      const { error } = await supabase.from('company_calendar_events').update(patch).eq('id', existing.id);
      if (error) return { success: false, error: error.message };
      return { success: true, eventId: existing.id };
    }

    const { data, error } = await supabase
      .from('company_calendar_events')
      .insert({
        ...patch,
        company_id: args.companyId,
        created_by_user_id: args.createdByUserId,
        chat_session_id: args.chatSessionId,
        start_time: window.startIso,
        end_time: window.endIso,
        duration: window.durationMinutes * 60,
        status: 'scheduled',
      })
      .select('id')
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, eventId: data.id };
  } catch (err: unknown) {
    console.error('Error upserting meeting calendar event:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Failed to save meeting on the calendar' };
  }
}

/**
 * Mirror a meeting to the creator's Google Calendar (with a Meet link when the room has one).
 * Returns the Google event id; the caller stores it on the company row so the real end can be written back.
 */
export async function createGoogleCalendarEventForMeeting(args: {
  title: string;
  description?: string;
  startIso: string;
  endIso: string;
  attendeeEmails: string[];
  meetUrl?: string | null;
  chatSessionId?: string | null;
  calendarEventId?: string | null;
}): Promise<{ success: boolean; eventId?: string; error?: string }> {
  try {
    const { currentUser } = useUserStore.getState();
    if (!currentUser?.companyId) return { success: false, error: 'User not authenticated' };
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data, error } = await supabase.functions.invoke('google-calendar-integration', {
      body: {
        action: 'create_event',
        created_by_user_id: currentUser.id,
        company_id: currentUser.companyId,
        event: {
          summary: args.title,
          description: [
            'Currents meeting',
            args.description || '',
            args.meetUrl ? `Meet: ${args.meetUrl}` : '',
            '',
            '(Ends when the last attendee leaves in Currents; this event updates to the real end.)',
          ]
            .filter(Boolean)
            .join('\n'),
          location: args.meetUrl || undefined,
          start: { dateTime: args.startIso, timeZone: tz },
          end: { dateTime: args.endIso, timeZone: tz },
          attendees: args.attendeeEmails.filter(Boolean).map((email) => ({ email })),
          colorId: getGoogleCalendarColorId('meeting'),
          extendedProperties: {
            private: {
              currents_source: 'meeting',
              currents_chat_session_id: args.chatSessionId ?? '',
              currents_calendar_event_id: args.calendarEventId ?? '',
            },
          },
        },
      },
    });
    if (error) return { success: false, error: error.message };
    if (data?.error) return { success: false, error: data.error };
    return { success: true, eventId: data?.eventId };
  } catch (err: unknown) {
    return { success: false, error: err instanceof Error ? err.message : 'Failed to create Google Calendar event' };
  }
}

/**
 * First attendee in: the row's start becomes now and it goes `active`. Later joiners leave it alone.
 * Safe to call with a Google event id (not a uuid) — it is a no-op then.
 */
export async function markMeetingCalendarRowJoined(calendarEventId?: string | null): Promise<void> {
  if (!calendarEventId || !UUID_RE.test(calendarEventId)) return;
  const { data: row } = await supabase
    .from('company_calendar_events')
    .select('id, status')
    .eq('id', calendarEventId)
    .maybeSingle();
  if (!row) return;
  const patch = realStartForCalendarRow(row, Date.now());
  if (!patch) return;
  await supabase
    .from('company_calendar_events')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', row.id);
}

/**
 * Attendee out: the row's end becomes now (first leaver) or extends (later leaver). When the row
 * carries a Google event, the Google end moves too, so "ran over / ended early" is on both calendars.
 */
export async function markMeetingCalendarRowLeft(calendarEventId?: string | null): Promise<void> {
  if (!calendarEventId || !UUID_RE.test(calendarEventId)) return;
  const { data: row } = await supabase
    .from('company_calendar_events')
    .select('id, status, start_time, end_time, google_event_id')
    .eq('id', calendarEventId)
    .maybeSingle();
  if (!row) return;
  const nowMs = Date.now();
  const patch = realEndForCalendarRow(row, nowMs);
  if (!patch) return;
  const startMs = new Date(row.start_time).getTime();
  const duration = Number.isFinite(startMs) ? Math.max(0, Math.round((nowMs - startMs) / 1000)) : undefined;
  await supabase
    .from('company_calendar_events')
    .update({ ...patch, duration, updated_at: new Date(nowMs).toISOString() })
    .eq('id', row.id);

  if (row.google_event_id) {
    const { currentUser } = useUserStore.getState();
    if (!currentUser?.companyId) return;
    const { error } = await supabase.functions.invoke('google-calendar-integration', {
      body: {
        action: 'update_event',
        created_by_user_id: currentUser.id,
        company_id: currentUser.companyId,
        event_id: row.google_event_id,
        updates: {
          end: { dateTime: patch.end_time, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        },
      },
    });
    if (error) console.warn('⚠️ Google end time not updated (meeting still closed):', error.message);
  }
}

/**
 * Fetch calendar events for a company
 */
export async function fetchCompanyCalendarEvents(
  companyId: string,
  options?: {
    startDate?: string; // ISO timestamp
    endDate?: string; // ISO timestamp
    showOnTeamCalendar?: boolean;
  }
): Promise<{ success: boolean; events?: CalendarEvent[]; error?: string }> {
  try {
    let query = supabase
      .from('company_calendar_events')
      .select('*')
      .eq('company_id', companyId)
      .order('start_time', { ascending: true });

    if (options?.startDate) {
      query = query.gte('start_time', options.startDate);
    }

    if (options?.endDate) {
      query = query.lte('start_time', options.endDate);
    }

    if (options?.showOnTeamCalendar !== undefined) {
      query = query.eq('show_on_team_calendar', options.showOnTeamCalendar);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching calendar events:', error);
      return { success: false, error: error.message };
    }

    return { success: true, events: data as CalendarEvent[] };
  } catch (err: any) {
    console.error('Error fetching calendar events:', err);
    return { success: false, error: err.message || 'Failed to fetch calendar events' };
  }
}

/**
 * Get Google Calendar color ID for activity type
 */
function getGoogleCalendarColorId(activityType: ActivityType): string {
  // Google Calendar color IDs
  // https://developers.google.com/calendar/api/v3/reference/colors
  const colorMap: Record<ActivityType, string> = {
    flow: '9', // Blue
    driving: '3', // Purple
    lunch: '6', // Orange
    vacation: '7', // Cyan/Turquoise
    holiday: '4', // Pink / rose
    ooo: '8', // Gray
    meeting: '2', // Green
    break: '5', // Yellow
    custom: '4', // Pink
  };
  return colorMap[activityType] || '9';
}

/**
 * Create Google Calendar event for a live activity
 */
export async function createGoogleCalendarEventFromActivity(options: {
  activityId: string;
  activityType: ActivityType;
  title: string;
  description?: string;
  startTime: string; // ISO timestamp
  endTime: string; // ISO timestamp (planned end time)
  icon: string; // Emoji icon
}): Promise<{ success: boolean; eventId?: string; error?: string }> {
  try {
    console.log('📅 [CLIENT] createGoogleCalendarEventFromActivity called with:', {
      activityId: options.activityId,
      activityType: options.activityType,
      title: options.title,
      startTime: options.startTime,
      endTime: options.endTime,
    });
    
    const { currentUser } = useUserStore.getState();
    if (!currentUser?.companyId) {
      console.error('❌ [CLIENT] User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }

    console.log('🔑 [CLIENT] User authenticated:', {
      userId: currentUser.id,
      companyId: currentUser.companyId,
    });

    const requestBody = {
      action: 'create_event',
      created_by_user_id: currentUser.id,
      company_id: currentUser.companyId,
      event: {
        summary: `${options.icon} ${options.title}`,
        description: [
          'Currents Real-Time Activity',
          '',
          options.description || '',
          '',
          `Expected duration: ${Math.round((new Date(options.endTime).getTime() - new Date(options.startTime).getTime()) / 60000)}m`,
          '',
          '(Actual end time may vary - this event will auto-update when the activity ends)',
        ].filter(Boolean).join('\n'),
        start: {
          dateTime: options.startTime,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: options.endTime,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        colorId: getGoogleCalendarColorId(options.activityType),
        extendedProperties: {
          private: {
            currents_activity_id: options.activityId,
            currents_source: 'live_activity',
          },
        },
      },
    };

    console.log('📤 [CLIENT] Calling Edge Function with:', JSON.stringify(requestBody, null, 2));

    // Call google-calendar-integration Edge Function
    const { data, error } = await supabase.functions.invoke('google-calendar-integration', {
      body: requestBody,
    });

    console.log('📥 [CLIENT] Edge Function response:', { data, error });

    if (error) {
      console.error('Error creating Google Calendar event:', error);
      return { success: false, error: error.message };
    }

    if (data?.error) {
      console.error('Google Calendar API error:', data.error);
      return { success: false, error: data.error };
    }

    console.log('✅ Google Calendar event created:', data.eventId);
    return { success: true, eventId: data.eventId };
  } catch (err: any) {
    console.error('Error creating Google Calendar event:', err);
    return { success: false, error: err.message || 'Failed to create Google Calendar event' };
  }
}

/**
 * Create a Google Calendar event from a card's schedule (scheduled date, start time, duration).
 * Used when the user clicks "Add to Google Calendar" on a task that has calendar data.
 */
export async function createGoogleCalendarEventFromCard(options: {
  cardId: string;
  title: string;
  scheduledDate: string; // YYYY-MM-DD
  scheduledStartTime?: string | null; // HH:mm or HH:mm:ss
  scheduledDurationMinutes?: number | null;
  ticketNumber?: number | null;
  projectKey?: string | null;
}): Promise<{ success: boolean; eventId?: string; error?: string }> {
  try {
    const { currentUser } = useUserStore.getState();
    if (!currentUser?.companyId) {
      return { success: false, error: 'User not authenticated' };
    }

    const dateStr = options.scheduledDate.trim();
    const timeStr = (options.scheduledStartTime || '09:00').trim();
    const durationMin = options.scheduledDurationMinutes ?? 60;

    const startISO = `${dateStr}T${timeStr.length === 5 ? timeStr + ':00' : timeStr}`;
    const startDate = new Date(startISO);
    if (isNaN(startDate.getTime())) {
      return { success: false, error: 'Invalid scheduled date or time' };
    }
    const endDate = new Date(startDate.getTime() + durationMin * 60 * 1000);
    const endISO = endDate.toISOString();

    const summary = options.ticketNumber != null && options.projectKey
      ? `${options.projectKey}-${options.ticketNumber} ${options.title}`
      : options.title;
    const description = [
      'Currents task',
      '',
      `Task: ${options.title}`,
      options.projectKey && options.ticketNumber != null ? `Ticket: ${options.projectKey}-${options.ticketNumber}` : '',
    ].filter(Boolean).join('\n');

    const requestBody = {
      action: 'create_event',
      created_by_user_id: currentUser.id,
      company_id: currentUser.companyId,
      event: {
        summary,
        description,
        start: {
          dateTime: startDate.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: endISO,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        extendedProperties: {
          private: {
            currents_card_id: options.cardId,
            currents_source: 'card_schedule',
          },
        },
      },
    };

    const { data, error } = await supabase.functions.invoke('google-calendar-integration', {
      body: requestBody,
    });

    if (error) return { success: false, error: error.message };
    if (data?.error) return { success: false, error: data.error };

    return { success: true, eventId: data?.eventId };
  } catch (err: any) {
    console.error('Error creating Google Calendar event from card:', err);
    return { success: false, error: err?.message || 'Failed to create Google Calendar event' };
  }
}

/**
 * Schedule a meeting from the Now calendar: a Google Calendar event with
 * attendees pre-filled from the current People selection. The edge function
 * passes the event body straight to Google, so attendees land on everyone's
 * calendar as an invitation (no notification email until sendUpdates ships).
 */
export async function createGoogleCalendarMeeting(options: {
  title: string;
  startISO: string;
  endISO: string;
  attendeeEmails: string[];
  description?: string;
}): Promise<{ success: boolean; eventId?: string; htmlLink?: string; error?: string }> {
  try {
    const { currentUser } = useUserStore.getState();
    if (!currentUser?.companyId) {
      return { success: false, error: 'User not authenticated' };
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data, error } = await supabase.functions.invoke('google-calendar-integration', {
      body: {
        action: 'create_event',
        created_by_user_id: currentUser.id,
        company_id: currentUser.companyId,
        // Real invites: attendees get the email, not just a silent calendar entry.
        // (No-op until the edge function with sendUpdates support is deployed.)
        send_updates: 'all',
        event: {
          summary: options.title,
          description: options.description || 'Scheduled from the Currents Now calendar.',
          start: { dateTime: options.startISO, timeZone },
          end: { dateTime: options.endISO, timeZone },
          attendees: options.attendeeEmails.map((email) => ({ email })),
          extendedProperties: {
            private: { currents_source: 'now_calendar_meeting' },
          },
        },
      },
    });

    if (error) return { success: false, error: error.message };
    if (data?.error) return { success: false, error: data.error };
    return { success: true, eventId: data?.eventId, htmlLink: data?.htmlLink };
  } catch (err) {
    console.error('Error scheduling meeting from Now calendar:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Failed to schedule meeting' };
  }
}

/**
 * Update Google Calendar event when activity ends
 */
export async function updateGoogleCalendarEventForActivity(options: {
  eventId: string;
  actualEndTime: string; // ISO timestamp
  plannedDurationMinutes: number;
  actualDurationMinutes: number;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const { currentUser } = useUserStore.getState();
    if (!currentUser?.companyId) {
      return { success: false, error: 'User not authenticated' };
    }

    const diff = options.actualDurationMinutes - options.plannedDurationMinutes;
    const diffText = diff > 0 
      ? `ran ${Math.round(diff)}m over planned ${options.plannedDurationMinutes}m`
      : diff < 0
      ? `finished ${Math.round(Math.abs(diff))}m early (planned ${options.plannedDurationMinutes}m)`
      : 'finished on time';

    // Call google-calendar-integration Edge Function
    const { data, error } = await supabase.functions.invoke('google-calendar-integration', {
      body: {
        action: 'update_event',
        created_by_user_id: currentUser.id,
        company_id: currentUser.companyId,
        event_id: options.eventId,
        updates: {
          end: {
            dateTime: options.actualEndTime,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          description: `Currents Real-Time Activity\n\nActual duration: ${options.actualDurationMinutes}m (${diffText})`,
        },
      },
    });

    if (error) {
      console.error('Error updating Google Calendar event:', error);
      return { success: false, error: error.message };
    }

    if (data?.error) {
      console.error('Google Calendar API error:', data.error);
      return { success: false, error: data.error };
    }

    console.log('✅ Google Calendar event updated:', options.eventId);
    return { success: true };
  } catch (err: any) {
    console.error('Error updating Google Calendar event:', err);
    return { success: false, error: err.message || 'Failed to update Google Calendar event' };
  }
}

/**
 * Fetch Google Calendar events for all team members
 * This aggregates calendar events from each team member's personal Google Calendar
 */
export interface TeamCalendarEvent extends GoogleCalendarEvent {
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
}

export async function fetchTeamGoogleCalendarEvents(
  teamId: string | null, // null = all company users
  timeMin?: string, // ISO timestamp
  timeMax?: string  // ISO timestamp
): Promise<{ success: boolean; events?: TeamCalendarEvent[]; error?: string }> {
  try {
    const { currentUser, allUsers } = useUserStore.getState();
    if (!currentUser?.companyId) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if the selected team is the "Company" team
    // Import teamStore dynamically to avoid circular dependency
    const useTeamStore = (await import('@/store/teamStore')).default;
    const { teams } = useTeamStore.getState();
    const selectedTeam = teams.find(t => t.id === teamId);
    const isCompanyView = selectedTeam?.name === 'Company';

    // Filter users by team (if teamId provided) and only include users with company email
    const companyDomain = currentUser?.email?.split('@')[1];
    
    console.log(`📊 [TEAM CAL] Filtering users. Total users: ${allUsers.length}, Company domain: ${companyDomain}, Team ID: ${teamId}, Is Company View: ${isCompanyView}`);
    
    const teamUsers = allUsers.filter(user => {
      // Only include users with email (not agents, and has email)
      if (!user.email || user.isAgent) {
        console.log(`  ❌ Excluded ${user.name}: ${!user.email ? 'no email' : 'is agent'}`);
        return false;
      }
      
      // If we don't have a company domain yet, include all users with emails
      if (!companyDomain) {
        if (isCompanyView) return true;
        else if (teamId) return user.team === teamId;
        return true;
      }
      
      // Only include users with company email domain
      const userDomain = user.email.split('@')[1];
      if (userDomain !== companyDomain) {
        console.log(`  ❌ Excluded ${user.name}: domain mismatch (${userDomain} !== ${companyDomain})`);
        return false;
      }
      
      // Filter by team
      if (isCompanyView) {
        // Company view: Show ALL users (assigned and unassigned)
        console.log(`  ✅ Included ${user.name} (${user.email}): Company view`);
        return true;
      } else if (teamId) {
        // Specific team view: Show users assigned to that team (primary OR private)
        const isPrimaryTeamMember = user.team === teamId;
        const isPrivateTeamMember = user.privateTeams?.includes(teamId) || false;
        const included = isPrimaryTeamMember || isPrivateTeamMember;
        console.log(`  ${included ? '✅' : '❌'} ${user.name}: team ${user.team} ${included ? '(primary or private)' : '!=='} ${teamId}`);
        return included;
      } else {
        // No team selected: show all
        console.log(`  ✅ Included ${user.name} (${user.email}): No team filter`);
        return true;
      }
    });

    console.log(`📊 [TEAM CAL] Filtered to ${teamUsers.length} users:`, teamUsers.map(u => u.email));

    if (teamUsers.length === 0) {
      console.warn('⚠️ [TEAM CAL] No users passed filtering - returning empty events array');
      return { success: true, events: [] };
    }

    // Create a map of email -> user for quick lookups
    const emailToUserMap = new Map(teamUsers.map(u => [u.email.toLowerCase(), u]));
    
    // Fetch calendar events for each user in parallel
    const fetchPromises = teamUsers.map(async (user) => {
      try {
        console.log(`📅 [TEAM CAL] Fetching calendar for ${user.name} (${user.email})`);
        
        // Use user's email as calendarId to fetch their calendar
        const result = await listGoogleCalendarEvents(user.email, timeMin, timeMax);
        
        if (result.success && result.events) {
          // Attach user info to each event AND parse attendees
          return result.events.map(event => {
            // Find all Currents users who are attendees of this event
            const attendeeEmails = (event.attendees || []).map(a => a.email.toLowerCase());
            const attendeeUsers = (event.attendees || [])
              .map(attendee => emailToUserMap.get(attendee.email.toLowerCase()))
              .filter(Boolean) as typeof teamUsers;
            
            // ALWAYS include the calendar owner if they're not already in attendees
            if (!attendeeUsers.find(u => u.email === user.email)) {
              attendeeUsers.push(user);
            }
            
            console.log(`📅 [EVENT] "${event.summary}" - Calendar owner: ${user.email}, Attendees: [${attendeeEmails.join(', ')}], Matched Currents users: ${attendeeUsers.length}`);
            
            // If the calendar owner is in attendees, use them as primary
            // Otherwise use the first attendee, or fall back to calendar owner
            const primaryUser = attendeeUsers.find(u => u.email === user.email) || attendeeUsers[0] || user;
            
            const teamEvent: TeamCalendarEvent = {
              ...event,
              userId: primaryUser.id,
              userName: primaryUser.name || primaryUser.email,
              userEmail: primaryUser.email,
              userAvatar: primaryUser.avatar,
            };
            
            // If multiple Currents users are attendees, mark as merged
            if (attendeeUsers.length > 1) {
              // Sort attendees: viewing user (currentUser) first, then others
              const sortedAttendees = [...attendeeUsers].sort((a, b) => {
                if (a.id === currentUser.id) return -1; // currentUser first
                if (b.id === currentUser.id) return 1;
                return 0; // Keep original order for others
              });
              
              // Debug: Log avatar data for each user
              console.log(`  🔍 [AVATAR DEBUG] Event "${event.summary}" attendees:`, sortedAttendees.map(u => ({
                name: u.name,
                email: u.email,
                avatar: u.avatar,
                hasAvatar: !!u.avatar,
                avatarLength: u.avatar?.length || 0
              })));
              
              (teamEvent as any).userIds = sortedAttendees.map(u => u.id);
              (teamEvent as any).userNames = sortedAttendees.map(u => u.name || u.email);
              (teamEvent as any).userEmails = sortedAttendees.map(u => u.email);
              (teamEvent as any).userAvatars = sortedAttendees.map(u => u.avatar);
              (teamEvent as any).isMerged = true;
              console.log(`  ✅ Merged event with ${sortedAttendees.length} Currents users: [${sortedAttendees.map(u => u.name).join(', ')}] (viewing user first)`);
            }
            
            return teamEvent;
          });
        }
        
        console.warn(`⚠️ [TEAM CAL] Failed to fetch calendar for ${user.email}:`, result.error);
        return [];
      } catch (err: any) {
        console.error(`❌ [TEAM CAL] Error fetching calendar for ${user.email}:`, err);
        return [];
      }
    });

    // Wait for all requests to complete
    const results = await Promise.all(fetchPromises);
    
    // Flatten all events into one array
    const allEvents = results.flat();
    
    // Sort by start time
    allEvents.sort((a, b) => {
      const aTime = new Date(a.start.dateTime || a.start.date || 0).getTime();
      const bTime = new Date(b.start.dateTime || b.start.date || 0).getTime();
      return aTime - bTime;
    });

    console.log(`✅ [TEAM CAL] Fetched ${allEvents.length} total events from ${teamUsers.length} team members`);

    return { success: true, events: allEvents };
  } catch (err: any) {
    console.error('❌ [TEAM CAL] Error fetching team calendar events:', err);
    return { success: false, error: err.message || 'Failed to fetch team calendar events' };
  }
}

/**
 * LCARS Real-Time Calendar: Calculate live state for a calendar event
 * Determines if event is past/present/future, calculates progress, overtime, etc.
 */
export interface LiveCalendarEventState {
  isLive: boolean;
  isPast: boolean;
  isFuture: boolean;
  isOvertime: boolean;
  isNearEnd: boolean;
  
  plannedDurationMinutes: number;
  elapsedMinutes: number;
  remainingMinutes: number;
  overtimeMinutes: number;
  
  fillPercentage: number; // 0-100
  overtimePercentage: number; // 0-50 (capped)
  
  state: 'planned' | 'live' | 'near-end' | 'overtime' | 'completed';
}

export function calculateLiveState(
  eventStart: string,
  eventEnd: string,
  liveActivityStartedAt?: string
): LiveCalendarEventState {
  const now = Date.now();
  const startTime = new Date(eventStart).getTime();
  const endTime = new Date(eventEnd).getTime();
  const plannedDuration = endTime - startTime;
  const plannedDurationMinutes = Math.floor(plannedDuration / 60000);
  
  // Determine basic state
  const isPast = now > endTime;
  const isFuture = now < startTime;
  
  // If no live activity, just return basic state
  if (!liveActivityStartedAt) {
    return {
      isLive: false,
      isPast,
      isFuture,
      isOvertime: false,
      isNearEnd: false,
      plannedDurationMinutes,
      elapsedMinutes: 0,
      remainingMinutes: plannedDurationMinutes,
      overtimeMinutes: 0,
      fillPercentage: isPast ? 100 : 0,
      overtimePercentage: 0,
      state: isPast ? 'completed' : 'planned',
    };
  }
  
  // Calculate progress based on live activity start time
  const actualStartTime = new Date(liveActivityStartedAt).getTime();
  const elapsed = now - actualStartTime;
  const elapsedMinutes = Math.floor(elapsed / 60000);
  
  // Calculate fill percentage (capped at 100 for the main block)
  const fillPercentage = Math.min((elapsed / plannedDuration) * 100, 100);
  
  // Check if overtime
  const isOvertime = elapsed > plannedDuration;
  const overtimeMinutes = isOvertime ? Math.floor((elapsed - plannedDuration) / 60000) : 0;
  
  // Calculate overtime percentage (relative to planned duration, capped at 50%)
  const overtimePercentage = isOvertime
    ? Math.min((overtimeMinutes / plannedDurationMinutes) * 100, 50)
    : 0;
  
  // Calculate remaining time
  const remainingMinutes = Math.max(0, Math.floor((plannedDuration - elapsed) / 60000));
  
  // Determine if near end (last 10% of planned time)
  const isNearEnd = fillPercentage > 90 && !isOvertime;
  
  // Determine detailed state
  let state: 'live' | 'near-end' | 'overtime' = 'live';
  if (isOvertime) {
    state = 'overtime';
  } else if (isNearEnd) {
    state = 'near-end';
  }
  
  return {
    isLive: true,
    isPast: false,
    isFuture: false,
    isOvertime,
    isNearEnd,
    plannedDurationMinutes,
    elapsedMinutes,
    remainingMinutes,
    overtimeMinutes,
    fillPercentage,
    overtimePercentage,
    state,
  };
}

/**
 * Get active live activities for calendar events
 * Returns a map of calendar event IDs to live activity data
 */
export async function getActiveLiveActivitiesForCalendar(): Promise<Map<string, any>> {
  try {
    const { data, error } = await supabase
      .from('live_activities')
      .select('*')
      .eq('status', 'active');
    
    if (error) {
      console.error('Error fetching live activities:', error);
      return new Map();
    }
    
    // Create map of calendar event ID -> live activity
    const activityMap = new Map();
    
    if (data) {
      data.forEach((activity) => {
        // Check if activity has linked calendar event in metadata
        const linkedEventId = activity.metadata?.linked_calendar_event_id;
        if (linkedEventId) {
          activityMap.set(linkedEventId, activity);
        }
      });
    }
    
    return activityMap;
  } catch (err) {
    console.error('Error fetching live activities for calendar:', err);
    return new Map();
  }
}

/**
 * TIME RECLAMATION SYSTEM
 * Phase 4: Automatically update Google Calendar when meetings end early
 * "Tu" (時) - Time as the ultimate currency
 */

interface TimeSavingsResult {
  success: boolean;
  timeSavedMinutes: number;
  freedTimeSlot?: {
    start: string;
    end: string;
  };
  error?: string;
}

/**
 * Round time to nearest 15 minutes for cleaner calendar updates
 */
function roundToNearest15Min(date: Date): Date {
  const minutes = date.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  const rounded = new Date(date);
  rounded.setMinutes(roundedMinutes);
  rounded.setSeconds(0);
  rounded.setMilliseconds(0);
  return rounded;
}

/**
 * End live activity and reclaim time by updating Google Calendar
 * This is the MAGIC function that saves Tu (時)!
 */
export async function endLiveActivityAndReclaimTime(
  liveActivityId: string,
  googleCalendarEventId: string,
  actualEndTime: Date
): Promise<TimeSavingsResult> {
  try {
    console.log('⏰ [TIME RECLAMATION] Starting...', {
      liveActivityId,
      googleCalendarEventId,
      actualEndTime: actualEndTime.toISOString(),
    });

    // 1. Get live activity to find planned end time
    const { data: liveActivity, error: activityError } = await supabase
      .from('live_activities')
      .select('*')
      .eq('id', liveActivityId)
      .single();

    if (activityError || !liveActivity) {
      console.error('❌ [TIME RECLAMATION] Live activity not found:', activityError);
      return { success: false, timeSavedMinutes: 0, error: 'Live activity not found' };
    }

    // 2. Get planned end time from metadata
    const plannedEndTime = liveActivity.metadata?.planned_end_time
      ? new Date(liveActivity.metadata.planned_end_time)
      : null;

    if (!plannedEndTime) {
      console.warn('⚠️ [TIME RECLAMATION] No planned end time found, just ending activity');
      // Just end the activity without calendar update
      await supabase
        .from('live_activities')
        .update({
          ended_at: actualEndTime.toISOString(),
          status: 'completed',
        })
        .eq('id', liveActivityId);

      return { success: true, timeSavedMinutes: 0 };
    }

    // 3. Calculate time saved
    const timeSavedMs = plannedEndTime.getTime() - actualEndTime.getTime();
    const timeSavedMinutes = Math.floor(timeSavedMs / 60000);

    console.log('📊 [TIME RECLAMATION] Time calculation:', {
      plannedEnd: plannedEndTime.toISOString(),
      actualEnd: actualEndTime.toISOString(),
      timeSavedMinutes,
    });

    // 4. Only update calendar if meeting ended EARLY (5+ min savings)
    if (timeSavedMinutes >= 5) {
      console.log('✨ [TIME RECLAMATION] Meeting ended early! Updating Google Calendar...');

      // Round to nearest 15min for cleaner calendar
      const roundedEndTime = roundToNearest15Min(actualEndTime);

      // 5. Update Google Calendar event via Edge Function
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) {
        throw new Error('No current user');
      }

      const { error } = await supabase.functions.invoke('google-calendar-integration', {
        body: {
          action: 'update_event',
          created_by_user_id: currentUser.id,
          company_id: currentUser.companyId,
          event_id: googleCalendarEventId,
          updates: {
            end: {
              dateTime: roundedEndTime.toISOString(),
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            description: `✨ Time Reclaimed: ${timeSavedMinutes}min saved via Currents Real-Time Tracking\n\nOriginal end time: ${plannedEndTime.toLocaleTimeString()}\nActual end time: ${actualEndTime.toLocaleTimeString()}`,
          },
        },
      });

      if (error) {
        console.error('❌ [TIME RECLAMATION] Failed to update Google Calendar:', error);
        // Continue anyway - we'll still end the activity
      } else {
        console.log('✅ [TIME RECLAMATION] Google Calendar updated successfully!');
      }

      // 6. End live activity
      await supabase
        .from('live_activities')
        .update({
          ended_at: actualEndTime.toISOString(),
          status: 'completed',
          metadata: {
            ...liveActivity.metadata,
            time_saved_minutes: timeSavedMinutes,
            calendar_updated: !error,
          },
        })
        .eq('id', liveActivityId);

      // 7. Log time savings for analytics (Tu Metrics!)
      await logTimeSavings({
        userId: currentUser.id,
        companyId: currentUser.companyId,
        eventId: googleCalendarEventId,
        liveActivityId,
        timeSavedMinutes,
        plannedEndTime: plannedEndTime.toISOString(),
        actualEndTime: actualEndTime.toISOString(),
      });

      console.log('🎉 [TIME RECLAMATION] Complete! Saved', timeSavedMinutes, 'minutes of Tu!');

      return {
        success: true,
        timeSavedMinutes,
        freedTimeSlot: {
          start: roundedEndTime.toISOString(),
          end: plannedEndTime.toISOString(),
        },
      };
    }

    // Meeting ended on time or late - just end activity
    console.log('⏱️ [TIME RECLAMATION] Meeting ended on time or late, no calendar update needed');

    await supabase
      .from('live_activities')
      .update({
        ended_at: actualEndTime.toISOString(),
        status: 'completed',
      })
      .eq('id', liveActivityId);

    return { success: true, timeSavedMinutes: 0 };
  } catch (err: any) {
    console.error('❌ [TIME RECLAMATION] Error:', err);
    return { success: false, timeSavedMinutes: 0, error: err.message };
  }
}

/**
 * Log time savings to database for Tu Metrics dashboard
 */
interface TimeSavingsLog {
  userId: string;
  // May be null/undefined for users not yet attached to a company; inserted as-is.
  companyId: string | null | undefined;
  eventId: string;
  liveActivityId: string;
  timeSavedMinutes: number;
  plannedEndTime: string;
  actualEndTime: string;
  // Optional context fields; no caller currently supplies them, so these columns
  // are inserted as null today.
  eventTitle?: string;
  nextMeetingTitle?: string;
  nextMeetingTime?: string;
}

async function logTimeSavings(log: TimeSavingsLog): Promise<void> {
  try {
    console.log('📈 [TU METRICS] Time savings logged:', log);

    // Insert into time_savings_log table
    const { error } = await supabase.from('time_savings_log').insert({
      user_id: log.userId,
      company_id: log.companyId,
      calendar_event_id: log.eventId,
      calendar_event_title: log.eventTitle,
      planned_end_time: log.plannedEndTime,
      actual_end_time: log.actualEndTime,
      minutes_saved: log.timeSavedMinutes,
      live_activity_id: log.liveActivityId,
      metadata: {
        next_meeting_title: log.nextMeetingTitle,
        next_meeting_time: log.nextMeetingTime,
      },
    });

    if (error) {
      console.error('❌ [TU METRICS] Failed to insert into database:', error);
      return;
    }

    console.log(`✅ [TU METRICS] Saved ${log.timeSavedMinutes}min to database`);
  } catch (err) {
    console.error('❌ [TU METRICS] Failed to log time savings:', err);
    // Don't throw - this is non-critical
  }
}

/**
 * Get next calendar event for overtime conflict detection
 */
export async function getNextCalendarEvent(
  _userId: string,
  afterTime: Date
): Promise<any | null> {
  try {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) return null;

    // Get events from Google Calendar
    const timeMin = afterTime.toISOString();
    const timeMax = new Date(afterTime.getTime() + 24 * 60 * 60 * 1000).toISOString(); // Next 24 hours

    const { data, error } = await supabase.functions.invoke('google-calendar-integration', {
      body: {
        action: 'list_events',
        created_by_user_id: currentUser.id,
        company_id: currentUser.companyId,
        time_min: timeMin,
        time_max: timeMax,
      },
    });

    if (error || !data?.events || data.events.length === 0) {
      return null;
    }

    // Return first event (sorted by start time)
    return data.events[0];
  } catch (err) {
    console.error('❌ [OVERTIME] Failed to get next calendar event:', err);
    return null;
  }
}

/**
 * OVERTIME ALERT SYSTEM
 * Phase 5: Detect conflicts and notify attendees when meetings run late
 */

export interface OvertimeConflict {
  currentActivity: any; // Live activity that's running over
  nextEvent: any; // Google Calendar event that's blocked
  overtimeMinutes: number; // How many minutes over planned end
  delayMinutes: number; // How late the next meeting will be
  affectedAttendees: Array<{
    email: string;
    displayName: string;
    responseStatus: string;
  }>;
  currentMeetingName: string;
  nextMeetingName: string;
  nextMeetingStart: string;
}

/**
 * Check if a live activity is causing an overtime conflict
 * Run this every minute for active live activities
 */
export async function checkOvertimeConflicts(
  liveActivity: any
): Promise<OvertimeConflict | null> {
  try {
    const now = Date.now();
    
    // Check if activity has planned end time
    const plannedEndTime = liveActivity.metadata?.planned_end_time
      ? new Date(liveActivity.metadata.planned_end_time).getTime()
      : null;

    if (!plannedEndTime) {
      return null; // No planned end time, can't detect conflict
    }

    // Check if we're overtime
    if (now <= plannedEndTime) {
      return null; // Still within planned time
    }

    const overtimeMinutes = Math.floor((now - plannedEndTime) / 60000);

    console.log('⚠️ [OVERTIME] Activity is running over:', {
      activityId: liveActivity.id,
      overtimeMinutes,
    });

    // Get user's next calendar event
    const nextEvent = await getNextCalendarEvent(
      liveActivity.user_id,
      new Date(plannedEndTime)
    );

    if (!nextEvent) {
      console.log('✅ [OVERTIME] No next event found, no conflict');
      return null; // No conflict
    }

    const nextEventStart = new Date(nextEvent.start?.dateTime || nextEvent.start?.date).getTime();
    
    // Check if next event is affected (starts before or shortly after current time)
    const bufferMinutes = 5; // 5 min buffer
    const isAffected = nextEventStart <= (now + bufferMinutes * 60000);

    if (!isAffected) {
      console.log('✅ [OVERTIME] Next event has enough buffer, no conflict');
      return null;
    }

    // Calculate delay
    const delayMinutes = Math.ceil((now - nextEventStart) / 60000);

    console.log('🚨 [OVERTIME] CONFLICT DETECTED!', {
      currentActivity: liveActivity.title,
      nextEvent: nextEvent.summary,
      overtimeMinutes,
      delayMinutes,
    });

    return {
      currentActivity: liveActivity,
      nextEvent,
      overtimeMinutes,
      delayMinutes: Math.max(0, delayMinutes),
      affectedAttendees: nextEvent.attendees || [],
      currentMeetingName: liveActivity.title || liveActivity.activity_type,
      nextMeetingName: nextEvent.summary,
      nextMeetingStart: nextEvent.start?.dateTime || nextEvent.start?.date,
    };
  } catch (err) {
    console.error('❌ [OVERTIME] Error checking conflicts:', err);
    return null;
  }
}

/**
 * Get Currents users from email list
 * Used to identify which attendees get in-app notifications vs. emails
 */
async function getCurrentsUsersFromEmails(
  emails: string[]
): Promise<Array<{ id: string; name: string; email: string }>> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, name, email')
      .in('email', emails);

    if (error) {
      console.error('❌ [OVERTIME] Failed to get Currents users:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('❌ [OVERTIME] Error getting Currents users:', err);
    return [];
  }
}

/**
 * Send notifications for overtime delay
 * - In-app notifications for Currents users
 * - Email notifications for external attendees
 */
export async function notifyOvertimeDelay(
  conflict: OvertimeConflict
): Promise<{ success: boolean; error?: string }> {
  try {
    const { nextEvent, delayMinutes, currentActivity, currentMeetingName, nextMeetingName } = conflict;
    
    console.log('📢 [OVERTIME] Sending delay notifications...', {
      nextMeeting: nextMeetingName,
      delayMinutes,
    });

    // Get Currents users in next meeting
    const attendeeEmails = nextEvent.attendees?.map((a: any) => a.email) || [];
    const currentsAttendees = await getCurrentsUsersFromEmails(attendeeEmails);

    console.log('👥 [OVERTIME] Found', currentsAttendees.length, 'Currents users');

    // Send in-app notifications to Currents users
    for (const attendee of currentsAttendees) {
      // Create notification in feed
      await supabase.from('feed').insert({
        company_id: currentActivity.company_id,
        user_id: currentActivity.user_id, // From user
        type: 'meeting_delayed',
        content: `is running ${delayMinutes}min late in "${currentMeetingName}". "${nextMeetingName}" will start soon.`,
        metadata: {
          delayMinutes,
          nextEventId: nextEvent.id,
          nextEventName: nextMeetingName,
          estimatedStartTime: new Date(Date.now() + 5 * 60000).toISOString(),
        },
        read: { [attendee.id]: false }, // Mark as unread for this attendee
      });

      console.log('✅ [OVERTIME] Sent in-app notification to:', attendee.name);
    }

    // Send email to external attendees
    const externalAttendees = nextEvent.attendees?.filter(
      (a: any) => !currentsAttendees.find(c => c.email === a.email)
    ) || [];

    console.log('📧 [OVERTIME] Found', externalAttendees.length, 'external attendees');

    for (const attendee of externalAttendees) {
      // TODO: Implement email sending via Edge Function
      // For now, just log
      console.log('📧 [OVERTIME] Would send email to:', attendee.email, {
        subject: `Meeting Update: ${nextMeetingName}`,
        message: `${currentActivity.user?.name || 'A team member'} is running ${delayMinutes}min late in their current meeting. They'll join "${nextMeetingName}" as soon as possible.`,
      });
    }

    return { success: true };
  } catch (err: any) {
    console.error('❌ [OVERTIME] Error sending notifications:', err);
    return { success: false, error: err.message };
  }
}

